import { SearchFocusTarget, User } from "@/App";
import { useApp } from "@/contexts/AppContext";
import { Header } from "./Header";
import { Footer } from "./Footer";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import {
  BarChart3,
  FolderTree,
  Globe,
  Layers3,
  MapPin,
  MousePointerClick,
  TrendingUp,
  Users,
} from "lucide-react";
import {
  fetchStatisticsOverview,
  fetchStatisticsGrowth,
  fetchStatisticsCategories,
  fetchStatisticsRegions,
  fetchStatisticsEngagement,
  type StatisticsOverviewDto,
  type StatisticsGrowthDto,
  type StatisticsCategoriesDto,
  type StatisticsRegionsDto,
  type StatisticsEngagementDto,
  type StatisticsRegionLayer,
} from "@/services/statistics.service";
import { OverviewCards } from "./statistics/OverviewCards";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "./ui/card";
import { MonthlyTrendsChart } from "./statistics/MonthlyTrendsChart";
import { CategoryTop10Bar } from "./statistics/CategoryTop10Bar";
import { CategoryDistributionPie } from "./statistics/CategoryDistributionPie";
import { CategoryDepthCoverage } from "./statistics/CategoryDepthCoverage";
import { DailyPageviewsChart } from "./statistics/DailyPageviewsChart";
import { RegionsLayerRanking } from "./statistics/RegionsLayerRanking";
import { Button } from "./ui/button";

interface StatisticsProps {
  navigate: (page: string) => void;
  currentUser: User | null;
  onLogout: () => void;
  onSearch: (query: string) => void;
  onNavigateToSearchPage: (target: SearchFocusTarget) => void;
}

type StatisticsTab =
  | "overview"
  | "growth"
  | "categories"
  | "regions"
  | "engagement";
type LoadState<T> = {
  loading: boolean;
  loaded: boolean;
  error: boolean;
  data: T | null;
};

const CATEGORY_SCALE = [
  "var(--category-scale-1)",
  "var(--category-scale-2)",
  "var(--category-scale-3)",
  "var(--category-scale-4)",
  "var(--category-scale-5)",
];
const REGION_LAYERS: StatisticsRegionLayer[] = [
  "state",
  "administrativeDistrict",
  "district",
  "postalCode",
];
const REGION_TOP_LIMIT = 20;
const REGION_STATE_LIMIT = 200;

const idle = <T,>(): LoadState<T> => ({
  loading: false,
  loaded: false,
  error: false,
  data: null,
});

export function Statistics({
  navigate,
  currentUser,
  onLogout,
  onSearch,
  onNavigateToSearchPage,
}: StatisticsProps) {
  void onSearch;
  const { t, language } = useApp();
  const mountedRef = useRef(true);
  const [activeTab, setActiveTab] = useState<StatisticsTab>("overview");
  const [regionsLayer, setRegionsLayer] = useState<StatisticsRegionLayer>("state");
  const [overviewState, setOverviewState] = useState<LoadState<StatisticsOverviewDto>>(idle());
  const [growthState, setGrowthState] = useState<LoadState<StatisticsGrowthDto>>(idle());
  const [categoriesState, setCategoriesState] = useState<LoadState<StatisticsCategoriesDto>>(idle());
  const [engagementState, setEngagementState] = useState<LoadState<StatisticsEngagementDto>>(idle());
  const [regionsStateByLayer, setRegionsStateByLayer] = useState<
    Record<StatisticsRegionLayer, LoadState<StatisticsRegionsDto>>
  >({
    state: idle(),
    administrativeDistrict: idle(),
    district: idle(),
    postalCode: idle(),
  });

  const normalizedLanguage = useMemo(
    () => ((language || "de").toLowerCase().startsWith("de") ? "de" : "en"),
    [language]
  );
  const numberLocale = normalizedLanguage === "de" ? "de-DE" : "en-US";

  const iconStyle = {
    color:
      "color-mix(in srgb, color-mix(in srgb, var(--primary) 70%, var(--accent)) 90%, var(--foreground))",
  } as const;
  const primaryTextStyle = {
    color: "color-mix(in srgb, var(--foreground) 77%, var(--surface))",
  } as const;
  const secondaryTextStyle = {
    color: "color-mix(in srgb, var(--foreground) 66%, var(--surface))",
  } as const;

  const layerButtonClass = "h-8";
  const activeLayerStyle = {
    "--layer-active-bg": "color-mix(in srgb, var(--accent) 70%, var(--foreground))",
    "--layer-active-bg-hover":
      "color-mix(in srgb, var(--accent) 60%, var(--foreground))",
    "--layer-active-border":
      "color-mix(in srgb, var(--accent) 78%, var(--foreground))",
    "--layer-active-text": "var(--background)",
    backgroundColor: "var(--layer-active-bg)",
    borderColor: "var(--layer-active-border)",
    color: "var(--layer-active-text)",
  } as CSSProperties;
  const activeLayerClass =
    "hover:[--layer-active-bg:var(--layer-active-bg-hover)] hover:text-[var(--layer-active-text)] dark:[--layer-active-bg:color-mix(in_srgb,var(--accent)_36%,var(--surface))] dark:[--layer-active-bg-hover:color-mix(in_srgb,var(--accent)_44%,var(--surface))] dark:[--layer-active-border:color-mix(in_srgb,var(--accent)_58%,var(--surface))] dark:[--layer-active-text:var(--foreground)]";

  const fmtInt = useCallback(
    (value: number) => Number(value || 0).toLocaleString(numberLocale),
    [numberLocale]
  );
  const fmtOne = useCallback(
    (value: number) =>
      Number(value || 0).toLocaleString(numberLocale, {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      }),
    [numberLocale]
  );
  const fmtDate = useCallback(
    (value: string | null | undefined) => {
      if (!value) return t("statistics.noData");
      const parsed = new Date(value);
      if (Number.isNaN(parsed.getTime())) return t("statistics.noData");
      return parsed.toLocaleDateString(numberLocale, {
        year: "numeric",
        month: "short",
        day: "2-digit",
      });
    },
    [numberLocale, t]
  );

  useEffect(() => {
    window.scrollTo(0, 0);
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const loadOverview = useCallback(async () => {
    setOverviewState((prev) =>
      prev.loaded || prev.loading ? prev : { ...prev, loading: true, error: false }
    );
    try {
      const data = await fetchStatisticsOverview();
      if (!mountedRef.current) return;
      setOverviewState({ loading: false, loaded: true, error: false, data });
    } catch {
      if (!mountedRef.current) return;
      setOverviewState((prev) => ({ ...prev, loading: false, error: true }));
    }
  }, []);

  const loadGrowth = useCallback(async () => {
    setGrowthState((prev) =>
      prev.loaded || prev.loading ? prev : { ...prev, loading: true, error: false }
    );
    try {
      const data = await fetchStatisticsGrowth();
      if (!mountedRef.current) return;
      setGrowthState({ loading: false, loaded: true, error: false, data });
    } catch {
      if (!mountedRef.current) return;
      setGrowthState((prev) => ({ ...prev, loading: false, error: true }));
    }
  }, []);

  const loadCategories = useCallback(async () => {
    setCategoriesState((prev) =>
      prev.loaded || prev.loading ? prev : { ...prev, loading: true, error: false }
    );
    try {
      const data = await fetchStatisticsCategories();
      if (!mountedRef.current) return;
      setCategoriesState({ loading: false, loaded: true, error: false, data });
    } catch {
      if (!mountedRef.current) return;
      setCategoriesState((prev) => ({ ...prev, loading: false, error: true }));
    }
  }, []);

  const loadRegions = useCallback(async (layer: StatisticsRegionLayer) => {
    setRegionsStateByLayer((prev) => {
      const current = prev[layer];
      if (current.loaded || current.loading) return prev;
      return { ...prev, [layer]: { ...current, loading: true, error: false } };
    });
    try {
      const requestLimit =
        layer === "state" ? REGION_STATE_LIMIT : REGION_TOP_LIMIT;
      const data = await fetchStatisticsRegions(layer, requestLimit);
      if (!mountedRef.current) return;
      setRegionsStateByLayer((prev) => ({
        ...prev,
        [layer]: { loading: false, loaded: true, error: false, data },
      }));
    } catch {
      if (!mountedRef.current) return;
      setRegionsStateByLayer((prev) => ({
        ...prev,
        [layer]: { ...prev[layer], loading: false, error: true },
      }));
    }
  }, []);

  const loadEngagement = useCallback(async () => {
    setEngagementState((prev) =>
      prev.loaded || prev.loading ? prev : { ...prev, loading: true, error: false }
    );
    try {
      const data = await fetchStatisticsEngagement(30);
      if (!mountedRef.current) return;
      setEngagementState({ loading: false, loaded: true, error: false, data });
    } catch {
      if (!mountedRef.current) return;
      setEngagementState((prev) => ({ ...prev, loading: false, error: true }));
    }
  }, []);

  useEffect(() => {
    if (activeTab === "overview") void loadOverview();
    if (activeTab === "growth") void loadGrowth();
    if (activeTab === "categories") void loadCategories();
    if (activeTab === "regions") void loadRegions(regionsLayer);
    if (activeTab === "engagement") void loadEngagement();
  }, [
    activeTab,
    regionsLayer,
    loadOverview,
    loadGrowth,
    loadCategories,
    loadRegions,
    loadEngagement,
  ]);

  const overview = overviewState.data;
  const growth = growthState.data;
  const categories = categoriesState.data;
  const engagement = engagementState.data;
  const activeRegionsState = regionsStateByLayer[regionsLayer];

  const overviewCards = useMemo(
    () => [
      {
        label: t("statistics.totalWebsites"),
        value: fmtInt(overview?.totalWebsites ?? 0),
        detail: `+${fmtInt(overview?.websitesLast30Days ?? 0)} ${t("statistics.last30Days")}`,
        icon: <Globe className="w-6 h-6" style={iconStyle} />,
      },
      {
        label: t("statistics.totalCategories"),
        value: fmtInt(overview?.totalCategories ?? 0),
        detail: t("statistics.totalCategoriesDetail"),
        icon: <FolderTree className="w-6 h-6" style={iconStyle} />,
      },
      {
        label: t("statistics.activeUsers"),
        value: fmtInt(overview?.totalUsers ?? 0),
        detail: t("statistics.registeredUsersDetail"),
        icon: <Users className="w-6 h-6" style={iconStyle} />,
      },
      {
        label: t("statistics.avgCategories"),
        value: fmtOne(overview?.avgCategoriesPerWebsite ?? 0),
        detail: t("statistics.perWebsite"),
        icon: <BarChart3 className="w-6 h-6" style={iconStyle} />,
      },
      {
        label: t("statistics.latestWebsiteAdded"),
        value: fmtDate(overview?.lastWebsiteAddedAt),
        detail: t("statistics.latestWebsiteAddedDetail"),
        icon: <Globe className="w-6 h-6" style={iconStyle} />,
      },
      {
        label: t("statistics.latestUserRegistered"),
        value: fmtDate(overview?.lastUserRegisteredAt),
        detail: t("statistics.latestUserRegisteredDetail"),
        icon: <Users className="w-6 h-6" style={iconStyle} />,
      },
      {
        label: t("statistics.topCategoryNow"),
        value: overview?.topCategory
          ? normalizedLanguage === "de"
            ? overview.topCategory.nameDe || overview.topCategory.slug
            : overview.topCategory.nameEn || overview.topCategory.slug
          : t("statistics.noData"),
        detail: overview?.topCategory
          ? t("statistics.topCategoryShareDetail")
              .replace("{{share}}", fmtOne(overview.topCategory.sharePercent))
              .replace("{{count}}", fmtInt(overview.topCategory.count))
          : t("statistics.noData"),
        icon: <FolderTree className="w-6 h-6" style={iconStyle} />,
      },
    ],
    [overview, t, fmtInt, fmtOne, fmtDate, normalizedLanguage]
  );

  const growthCards = useMemo(
    () => [
      {
        label: t("statistics.websitesLast7Days"),
        value: fmtInt(growth?.websitesLast7Days ?? 0),
        detail: `${t("statistics.previous7Days")}: ${fmtInt(
          growth?.websitesPrevious7Days ?? 0
        )}`,
        icon: <TrendingUp className="w-6 h-6" style={iconStyle} />,
      },
      {
        label: t("statistics.websitesLast30DaysLabel"),
        value: fmtInt(growth?.websitesLast30Days ?? 0),
        detail: t("statistics.newWebsites"),
        icon: <Globe className="w-6 h-6" style={iconStyle} />,
      },
      {
        label: t("statistics.usersLast7Days"),
        value: fmtInt(growth?.usersLast7Days ?? 0),
        detail: `${t("statistics.previous7Days")}: ${fmtInt(
          growth?.usersPrevious7Days ?? 0
        )}`,
        icon: <Users className="w-6 h-6" style={iconStyle} />,
      },
      {
        label: t("statistics.usersLast30Days"),
        value: fmtInt(growth?.usersLast30Days ?? 0),
        detail: t("statistics.newUsers"),
        icon: <Users className="w-6 h-6" style={iconStyle} />,
      },
    ],
    [growth, t, fmtInt]
  );

  const engagementCards = useMemo(
    () => [
      {
        label: t("statistics.pageviewsLast7Days"),
        value: fmtInt(engagement?.pageviewsLast7Days ?? 0),
        detail: t("statistics.pageviewsLabel"),
        icon: <MousePointerClick className="w-6 h-6" style={iconStyle} />,
      },
      {
        label: t("statistics.pageviewsLast30Days"),
        value: fmtInt(engagement?.pageviewsLast30Days ?? 0),
        detail: t("statistics.pageviewsLabel"),
        icon: <MousePointerClick className="w-6 h-6" style={iconStyle} />,
      },
      {
        label: t("statistics.avgDailyPageviewsLast7Days"),
        value: fmtOne(engagement?.avgDailyPageviewsLast7Days ?? 0),
        detail: t("statistics.pageviewsLabel"),
        icon: <BarChart3 className="w-6 h-6" style={iconStyle} />,
      },
      {
        label: t("statistics.peakPageviewsDay"),
        value: fmtInt(engagement?.peakPageviewsDay?.pageviews ?? 0),
        detail: (t("statistics.peakOnDate") || "{{date}}" ).replace(
          "{{date}}",
          engagement?.peakPageviewsDay?.date
            ? String(engagement.peakPageviewsDay.date)
            : "-"
        ),
        icon: <Layers3 className="w-6 h-6" style={iconStyle} />,
      },
    ],
    [engagement, t, fmtInt, fmtOne]
  );

  const categoryDistributionPie = useMemo(() => {
    const source = categories?.categoryDistribution ?? [];
    const isGerman = normalizedLanguage === "de";
    return source.map((entry, index) => ({
      name: isGerman ? entry.nameDe || entry.slug : entry.nameEn || entry.slug,
      value: entry.count,
      color: CATEGORY_SCALE[index % CATEGORY_SCALE.length],
    }));
  }, [categories, normalizedLanguage]);

  const categoryTop10 = useMemo(() => {
    const source = categories?.categoryDistribution ?? [];
    const isGerman = normalizedLanguage === "de";
    const total = source.reduce((sum, item) => sum + item.count, 0);
    return source.slice(0, 10).map((entry, index) => ({
      name: isGerman ? entry.nameDe || entry.slug : entry.nameEn || entry.slug,
      count: entry.count,
      percent: total > 0 ? (entry.count / total) * 100 : 0,
      color: CATEGORY_SCALE[index % CATEGORY_SCALE.length],
    }));
  }, [categories, normalizedLanguage]);

  const categoryDepthCoverage = useMemo(
    () => [...(categories?.categoryDepthCoverage ?? [])].sort((a, b) => a.depth - b.depth),
    [categories]
  );

  const selectedLayerLabel = t(`statistics.layer.${regionsLayer}`);
  const isStateLayer = regionsLayer === "state";
  const shownWebsiteCount = useMemo(
    () =>
      (activeRegionsState.data?.items ?? []).reduce(
        (sum, item) => sum + item.count,
        0
      ),
    [activeRegionsState.data]
  );
  const topCoverageText = t("statistics.shownVsTotal")
    .replace("{{shown}}", shownWebsiteCount.toLocaleString(numberLocale))
    .replace(
      "{{total}}",
      (activeRegionsState.data?.totalInLayer ?? 0).toLocaleString(numberLocale)
    );
  const topLimitText = t("statistics.topOnlyLayerHint")
    .replace("{{limit}}", String(REGION_TOP_LIMIT))
    .replace("{{layer}}", selectedLayerLabel);

  return (
    <div
      className="flex flex-col min-h-screen"
      style={{ backgroundColor: "var(--background)" }}
    >
      <Header
        navigate={navigate}
        currentUser={currentUser}
        onLogout={onLogout}
        currentPage="statistics"
        samePageHandlers={{
          statistics: () =>
            typeof window !== "undefined" &&
            window.scrollTo({ top: 0, behavior: "smooth" }),
        }}
        onNavigateToSearchPage={onNavigateToSearchPage}
      />

      <main className="flex-1 py-8">
        <div className="container mx-auto px-4">
          <div className="mb-8">
            <h2 className="text-primary-heading mb-2">{t("statistics.title")}</h2>
            <p style={secondaryTextStyle}>{t("statistics.subtitle")}</p>
          </div>

          <Tabs
            value={activeTab}
            onValueChange={(value) => setActiveTab(value as StatisticsTab)}
            className="w-full"
          >
            <TabsList className="tabs-list h-auto w-full justify-start gap-2 p-2 overflow-x-auto">
              <TabsTrigger value="overview" className="tabs-trigger flex-none gap-2">
                <Globe className="w-4 h-4" />
                {t("statistics.overviewTab")}
              </TabsTrigger>
              <TabsTrigger value="growth" className="tabs-trigger flex-none gap-2">
                <TrendingUp className="w-4 h-4" />
                {t("statistics.growth")}
              </TabsTrigger>
              <TabsTrigger value="categories" className="tabs-trigger flex-none gap-2">
                <FolderTree className="w-4 h-4" />
                {t("statistics.categoriesTab")}
              </TabsTrigger>
              <TabsTrigger value="regions" className="tabs-trigger flex-none gap-2">
                <MapPin className="w-4 h-4" />
                {t("statistics.regions")}
              </TabsTrigger>
              <TabsTrigger value="engagement" className="tabs-trigger flex-none gap-2">
                <MousePointerClick className="w-4 h-4" />
                {t("statistics.engagementTab")}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="mt-6">
              {overviewState.error && (
                <div className="text-sm mb-4" style={primaryTextStyle}>
                  {t("statistics.failedToLoad")}
                </div>
              )}

              <OverviewCards cards={overviewCards} loading={overviewState.loading} />
            </TabsContent>

            <TabsContent value="growth" className="mt-6 space-y-6">
              <Card className="stat-card">
                <CardHeader>
                  <CardTitle className="text-lg font-semibold" style={primaryTextStyle}>
                    {t("statistics.monthlyTrends")}
                  </CardTitle>
                  <CardDescription style={secondaryTextStyle}>
                    {t("statistics.monthlyTrendsDesc")}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {growthState.loading && (
                    <div className="text-sm" style={secondaryTextStyle}>
                      {t("common.loading")}
                    </div>
                  )}
                  {growthState.error && (
                    <div className="text-sm" style={primaryTextStyle}>
                      {t("statistics.failedToLoad")}
                    </div>
                  )}
                  {growthState.loaded && growth && (
                    <MonthlyTrendsChart
                      data={growth.monthlyTrends}
                      t={t}
                      lang={normalizedLanguage}
                      websitesLineColor={CATEGORY_SCALE[2]}
                      usersLineColor={CATEGORY_SCALE[4]}
                    />
                  )}
                </CardContent>
              </Card>

              <OverviewCards cards={growthCards} loading={growthState.loading} />
            </TabsContent>

            <TabsContent value="categories" className="mt-6 space-y-6">
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                <Card className="stat-card">
                  <CardHeader>
                    <CardTitle className="text-lg font-semibold" style={primaryTextStyle}>
                      {t("statistics.categoryDistribution")}
                    </CardTitle>
                    <CardDescription style={secondaryTextStyle}>
                      {t("statistics.categoryDistributionDesc")}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {categoriesState.loading && (
                      <div className="text-sm" style={secondaryTextStyle}>
                        {t("common.loading")}
                      </div>
                    )}
                    {categoriesState.error && (
                      <div className="text-sm" style={primaryTextStyle}>
                        {t("statistics.failedToLoad")}
                      </div>
                    )}
                    {categoriesState.loaded && categories && (
                      <CategoryDistributionPie
                        data={categoryDistributionPie}
                        fallbackLabel={t("statistics.categoryLabel")}
                      />
                    )}
                  </CardContent>
                </Card>

                <Card className="stat-card">
                  <CardHeader>
                    <CardTitle className="text-lg font-semibold" style={primaryTextStyle}>
                      {t("statistics.categoryTop10")}
                    </CardTitle>
                    <CardDescription style={secondaryTextStyle}>
                      {t("statistics.categoryTop10Desc")}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {categoriesState.loading && (
                      <div className="text-sm" style={secondaryTextStyle}>
                        {t("common.loading")}
                      </div>
                    )}
                    {categoriesState.error && (
                      <div className="text-sm" style={primaryTextStyle}>
                        {t("statistics.failedToLoad")}
                      </div>
                    )}
                    {categoriesState.loaded && categories && (
                      <CategoryTop10Bar
                        data={categoryTop10}
                        noDataLabel={t("statistics.noData")}
                      />
                    )}
                  </CardContent>
                </Card>
              </div>

              <Card className="stat-card">
                <CardHeader>
                  <CardTitle className="text-lg font-semibold" style={primaryTextStyle}>
                    {t("statistics.categoryDepthCoverage")}
                  </CardTitle>
                  <CardDescription style={secondaryTextStyle}>
                    {t("statistics.categoryDepthCoverageDesc")}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {categoriesState.loading && (
                    <div className="text-sm" style={secondaryTextStyle}>
                      {t("common.loading")}
                    </div>
                  )}
                  {categoriesState.error && (
                    <div className="text-sm" style={primaryTextStyle}>
                      {t("statistics.failedToLoad")}
                    </div>
                  )}
                  {categoriesState.loaded && categories && (
                    <CategoryDepthCoverage
                      data={categoryDepthCoverage}
                      t={t}
                      locale={numberLocale}
                    />
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="regions" className="mt-6">
              <Card className="stat-card">
                <CardHeader>
                  <CardTitle className="text-lg font-semibold" style={primaryTextStyle}>
                    {t("statistics.regionalBreakdown")}
                  </CardTitle>
                  <CardDescription style={secondaryTextStyle}>
                    {t("statistics.regionalBreakdownDesc")}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-medium" style={primaryTextStyle}>
                      {t("statistics.layerLabel")}
                    </span>
                    {REGION_LAYERS.map((layer) => (
                      <Button
                        key={layer}
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setRegionsLayer(layer)}
                        className={
                          layer === regionsLayer
                            ? `${layerButtonClass} ${activeLayerClass}`
                            : layerButtonClass
                        }
                        style={layer === regionsLayer ? activeLayerStyle : primaryTextStyle}
                      >
                        {t(`statistics.layer.${layer}`)}
                      </Button>
                    ))}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {!isStateLayer && (
                      <Card className="stat-card">
                        <CardHeader className="pb-2">
                          <CardDescription style={secondaryTextStyle}>
                            {topLimitText}
                          </CardDescription>
                        </CardHeader>
                        <CardContent>
                          <div className="flex items-center gap-3">
                            <div className="icon-box">
                              <Layers3 className="w-6 h-6" style={iconStyle} />
                            </div>
                            <p className="text-primary-heading">{topCoverageText}</p>
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    <Card className="stat-card">
                      <CardHeader className="pb-2">
                        <CardDescription style={secondaryTextStyle}>
                          {t("statistics.totalInSelectedLayer")}
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="flex items-center gap-3">
                          <div className="icon-box">
                            <MapPin className="w-6 h-6" style={iconStyle} />
                          </div>
                          <p className="text-primary-heading">
                            {fmtInt(activeRegionsState.data?.totalInLayer ?? 0)}
                          </p>
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {activeRegionsState.loading && (
                    <div className="text-sm" style={secondaryTextStyle}>
                      {t("common.loading")}
                    </div>
                  )}
                  {activeRegionsState.error && (
                    <div className="text-sm" style={primaryTextStyle}>
                      {t("statistics.failedToLoad")}
                    </div>
                  )}
                  {activeRegionsState.loaded && activeRegionsState.data && (
                    <RegionsLayerRanking
                      items={activeRegionsState.data.items}
                      totalInLayer={activeRegionsState.data.totalInLayer}
                      t={t}
                      locale={numberLocale}
                    />
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="engagement" className="mt-6 space-y-6">
              <Card className="stat-card">
                <CardHeader>
                  <CardTitle className="text-lg font-semibold" style={primaryTextStyle}>
                    {t("statistics.dailyPageviewsTitle")}
                  </CardTitle>
                  <CardDescription style={secondaryTextStyle}>
                    {t("statistics.dailyPageviewsDesc")}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {engagementState.loading && (
                    <div className="text-sm" style={secondaryTextStyle}>
                      {t("common.loading")}
                    </div>
                  )}
                  {engagementState.error && (
                    <div className="text-sm" style={primaryTextStyle}>
                      {t("statistics.dailyPageviewsFailed")}
                    </div>
                  )}
                  {engagementState.loaded && engagement && (
                    <DailyPageviewsChart
                      data={engagement.dailyPageviews}
                      t={t}
                      lineColor={CATEGORY_SCALE[2]}
                    />
                  )}
                </CardContent>
              </Card>

              <OverviewCards cards={engagementCards} loading={engagementState.loading} />
            </TabsContent>
          </Tabs>
        </div>
      </main>

      <Footer navigate={navigate} />
    </div>
  );
}
